import React, { useState, useEffect } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import editProfileUser from "assets/images/others/host-side-images/editProfileUSer.png";
import useOutsideClick from "hooks/click-outside";
import useHook from "../useHooks";
import { RegisterRsvpDTO } from "utils/helpers/models/host-dtos/register-rsvp.dto";
import { concatPathWithBackendUrl } from "utils/helpers/urlHelper";
import { warningToaster } from "utils/helpers/common/alert-service";
import { ReactComponent as EditProfile } from "assets/images/svgs/edit-profiles.svg";

type UpdateRepresentativeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  representative: RegisterRsvpDTO | null;
  onUpdateComplete?: () => void;
};

const UpdateRepresentativeModal: React.FC<UpdateRepresentativeModalProps> = ({
  isOpen,
  onClose,
  representative,
  onUpdateComplete,
}) => {
  const [formData, setFormData] = useState<RegisterRsvpDTO>(
    new RegisterRsvpDTO()
  );
  const { updateRsvpByID, uploadProfile } = useHook();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    getValues,
    setValue,
  } = useForm<RegisterRsvpDTO>();

  useEffect(() => {
    if (representative) {
      setFormData(representative);
      reset(representative);
    }
  }, [representative, reset]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.target;
    setValue(name as keyof RegisterRsvpDTO, value, { shouldValidate: true });
    setFormData({ ...formData, [name]: value });
  };

  const onSubmit: SubmitHandler<RegisterRsvpDTO> = async () => {
    const profilePic = getValues("profilePicUrl");
    if (!profilePic) {
      warningToaster("Please upload your profile picture");
      return;
    }
    if (typeof profilePic !== "string" && profilePic instanceof Blob) {
      const form_data = new FormData();
      form_data.append("file", profilePic);
      const imageData: any = await uploadProfile(form_data);
      formData.profilePicId = imageData?.id;
    }

    if (formData.id) {
      await updateRsvpByID(formData.id, formData);
    }
    if (onUpdateComplete) {
      onUpdateComplete();
    }
    onClose();
    reset();
  };

  const uploadProfilePic = (event: any) => {
    const { files } = event.target;
    const file = files[0];
    setValue("profilePicUrl", file);
    setFormData({ ...formData, profilePicUrl: URL.createObjectURL(file) });
  };

  const modalRef = useOutsideClick(onClose);

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center transition-opacity duration-300 ${
        isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
      style={{ zIndex: 'calc(var(--z-modal) + 1)' }}
    >
      <div
        ref={modalRef}
        className={`bg-white px-6 py-8 rounded-lg shadow-lg w-[90%] sm:w-[440px] transform transition-transform duration-300 ${
          isOpen ? "scale-100" : "scale-95"
        }`}
      >
        <h2 className="text-xl text-center text-primaryBlueText font-bold mb-4">
          Update Representative
        </h2>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col items-center w-full">
            <div className="relative">
              <img
                src={
                  formData.profilePicUrl
                    ? typeof formData.profilePicUrl === "string"
                      ? formData.profilePicUrl
                      : concatPathWithBackendUrl(
                          formData.profilePicUrl?.fileDownloadUri
                        )
                    : editProfileUser
                }
                alt="Profile"
                className="w-36 h-36 rounded-full object-cover"
              />
              <label
                htmlFor="profilePicUrl"
                className="absolute bottom-2 right-0 bg-white shadow p-2 rounded-full cursor-pointer"
              >
                <EditProfile />
              </label>
              <input
                id="profilePicUrl"
                type="file"
                accept="image/*"
                className="hidden"
                {...register("profilePicUrl", { required: false })}
                onChange={uploadProfilePic}
              />
            </div>
            {errors.profilePicUrl && (
              <p className="text-red-500 text-sm mt-1">
                {errors.profilePicUrl.message}
              </p>
            )}
          </div>

          <div className="w-full my-4 grid grid-cols-1 gap-3">
            <div className="flex flex-col gap-1">
              <label className="custom-label">First Name</label>
              <input
                type="text"
                className="custom-input"
                {...register("firstName", {
                  required: "First Name is required",
                })}
                value={formData.firstName || ""}
                onChange={handleChange}
              />
              {errors.firstName && (
                <p className="text-red-500 text-sm">
                  {errors.firstName.message}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <label className="custom-label">Last Name</label>
              <input
                type="text"
                className="custom-input"
                {...register("lastName", {
                  required: "Last Name is required",
                })}
                value={formData.lastName || ""}
                onChange={handleChange}
              />
              {errors.lastName && (
                <p className="text-red-500 text-sm">
                  {errors.lastName.message}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <label className="custom-label">Email</label>
              <input
                type="email"
                className="custom-input"
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^\S+@\S+$/i,
                    message: "Enter a valid email",
                  },
                })}
                value={formData.email || ""}
                onChange={handleChange}
              />
              {errors.email && (
                <p className="text-red-500 text-sm">{errors.email.message}</p>
              )}
            </div>
          </div>

          <div className="mt-6">
            {/* <button
              onClick={() => handleDelete(formData.id)}
              type="button"
              className="h-[62px] bg-borderColor items-center px-4 py-1 rounded-full text-primaryBlueText mb-3"
            >
              Delete User
            </button> */}
            <button
              type="submit"
              className="h-[62px] text-base bg-primaryBlueBg rounded-full text-white w-full"
            >
              Update
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateRepresentativeModal;
